import { z } from 'zod';
import { StatoFase, StatoProgetto, StatoScadenza } from './enums';
import {
  dataOraSchema,
  dataSchema,
  idSchema,
  idSelezioneSchema,
  importoSchema,
  indirizzoSchema,
  paginationQuerySchema,
} from '../common';

/** Il progetto (pratica) è il cuore operativo: un cliente, N fasi, N scadenze. */

export const progettoCreateSchema = z.object({
  /** Progressivo generato automaticamente ma sempre modificabile. */
  codiceInterno: z.string().trim().max(40).optional(),
  nome: z.string().trim().min(1).max(255),
  descrizione: z.string().trim().max(10_000).optional(),
  clienteId: idSelezioneSchema('Seleziona un cliente'),
  /** Etichetta storica: se il template viene cancellato il progetto resta. */
  templateOrigineId: idSelezioneSchema('Seleziona un template').optional(),
  tipologiaPratica: z.string().trim().max(120).optional(),
  stato: StatoProgetto.default('Attivo'),
  valoreTeoricoInvestimento: importoSchema.optional(),
  valoreTeoricoAgevolazione: importoSchema.optional(),
  /** Obbligatoria in creazione: almeno una sede del cliente. */
  sedeClienteIds: z.array(idSchema).min(1, 'Indica almeno una sede del cliente'),
  clientePersonaIds: z.array(idSchema).default([]),
  /** Vuoto ⇒ il progetto è "Diretto". */
  partnerPersonaIds: z.array(idSchema).default([]),
  /** Gli utenti collegati definiscono anche il team del progetto. */
  utenteIds: z.array(idSchema).min(1, 'Indica almeno un utente collegato'),
});
export type ProgettoCreate = z.infer<typeof progettoCreateSchema>;

/**
 * Aggiornamento standard: NON tocca `tipologiaPratica` (corretta solo tramite
 * l'azione dedicata) né le collezioni N:N impostate in creazione
 * (sedi/persone/utenti collegati restano gestite altrove, non da un PATCH
 * generico — stesso principio del codice fiscale in Clienti).
 */
export const progettoUpdateSchema = progettoCreateSchema
  .omit({
    clienteId: true,
    templateOrigineId: true,
    tipologiaPratica: true,
    sedeClienteIds: true,
    clientePersonaIds: true,
    partnerPersonaIds: true,
    utenteIds: true,
  })
  .partial()
  // `.partial()` non basta sui campi con `.default(...)`: Zod applica comunque
  // il default quando la chiave è assente, quindi un PATCH che omette `stato`
  // lo resetterebbe silenziosamente ad "Attivo". Si ridichiara qui senza default.
  .extend({ stato: StatoProgetto.optional() });
export type ProgettoUpdate = z.infer<typeof progettoUpdateSchema>;

/** Regole di rigidità ereditate dal template, modificabili con MANAGE_PHASE_RULES. */
export const regoleFasiSchema = z.object({
  abilitaCreazioneFasi: z.boolean().default(true),
  permettiEliminazioneFasi: z.boolean().default(true),
  permettiModificaFasi: z.boolean().default(true),
});
export type RegoleFasi = z.infer<typeof regoleFasiSchema>;

export const progettoSchema = z.object({
  id: idSchema,
  codiceInterno: z.string(),
  nome: z.string(),
  descrizione: z.string().nullable(),
  clienteId: idSchema,
  templateOrigineId: idSchema.nullable(),
  templateOrigineNome: z.string().nullable(),
  tipologiaPratica: z.string().nullable(),
  stato: StatoProgetto,
  valoreTeoricoInvestimento: importoSchema.nullable(),
  valoreTeoricoAgevolazione: importoSchema.nullable(),
  /** Espone le regole al client SOLO per abilitare/disabilitare la UI: l'applicazione vera resta nel service. */
  regoleFasi: regoleFasiSchema,
  creatoDa: idSchema.nullable(),
  creatoIl: dataOraSchema,
});
export type Progetto = z.infer<typeof progettoSchema>;

/** Riga della lista progetti: i pochi campi per scansionare l'elenco. */
export const progettoListItemSchema = z.object({
  id: idSchema,
  codiceInterno: z.string(),
  nome: z.string(),
  clienteId: idSchema,
  clienteRagioneSociale: z.string(),
  tipologiaPratica: z.string().nullable(),
  stato: StatoProgetto,
  creatoIl: dataOraSchema,
});
export type ProgettoListItem = z.infer<typeof progettoListItemSchema>;

export const progettoListQuerySchema = paginationQuerySchema.extend({
  stato: StatoProgetto.optional(),
  clienteId: idSchema.optional(),
  /** Progetti a cui partecipa almeno una persona di questo partner. */
  partnerId: idSchema.optional(),
});
export type ProgettoListQuery = z.infer<typeof progettoListQuerySchema>;

/**
 * Correzione riservata della tipologia pratica: azione distinta
 * dall'update standard, motivazione obbligatoria tracciata in `audit_log`.
 * Stesso pattern di `correzioneCodiceFiscaleSchema`.
 */
export const correzioneTipologiaPraticaSchema = z.object({
  tipologiaPratica: z.string().trim().min(1).max(120),
  motivazione: z.string().trim().min(10, 'La motivazione deve avere almeno 10 caratteri'),
});
export type CorrezioneTipologiaPratica = z.infer<typeof correzioneTipologiaPraticaSchema>;

/** Utenti collegati al progetto (= il team) — vista compatta per la UI. */
export const progettoUtenteSchema = z.object({
  id: idSchema,
  nome: z.string(),
  cognome: z.string(),
  email: z.string(),
});
export type ProgettoUtente = z.infer<typeof progettoUtenteSchema>;

export const progettoUtentiSetSchema = z.object({
  utenteIds: z.array(idSchema).min(1, 'Indica almeno un utente collegato'),
});
export type ProgettoUtentiSet = z.infer<typeof progettoUtentiSetSchema>;

/* ── Riferimenti del progetto (sedi/persone-cliente/persone-partner) ─────── */

/** Sede del cliente coinvolta nel progetto — vista compatta per la UI. */
export const progettoSedeSchema = z.object({
  id: idSchema,
  etichetta: z.string(),
  /** Serve a mostrare l'unità locale e ad aprirla nelle mappe senza una seconda chiamata. */
  indirizzo: indirizzoSchema,
  /** Copia della sede legale fra le operative: la rappresenta nei selettori di sede. */
  daSedeLegale: z.boolean(),
});
export type ProgettoSede = z.infer<typeof progettoSedeSchema>;

export const progettoSediSetSchema = z.object({
  sedeClienteIds: z.array(idSchema).min(1, 'Indica almeno una sede del cliente'),
});
export type ProgettoSediSet = z.infer<typeof progettoSediSetSchema>;

/** Persona del cliente di riferimento nel progetto — vista compatta per la UI. */
export const progettoPersonaClienteSchema = z.object({
  id: idSchema,
  nome: z.string(),
  cognome: z.string(),
});
export type ProgettoPersonaCliente = z.infer<typeof progettoPersonaClienteSchema>;

export const progettoPersoneClienteSetSchema = z.object({
  clientePersonaIds: z.array(idSchema),
});
export type ProgettoPersoneClienteSet = z.infer<typeof progettoPersoneClienteSetSchema>;

/** Persona-partner di riferimento esterno nel progetto — vista compatta per la UI. */
export const progettoPersonaPartnerSchema = z.object({
  id: idSchema,
  nome: z.string(),
  cognome: z.string(),
  partnerId: idSchema,
  partnerRagioneSociale: z.string(),
});
export type ProgettoPersonaPartner = z.infer<typeof progettoPersonaPartnerSchema>;

/** Vuoto ⇒ il progetto è "Diretto": nessun minimo richiesto. */
export const progettoPersonePartnerSetSchema = z.object({
  partnerPersonaIds: z.array(idSchema),
});
export type ProgettoPersonePartnerSet = z.infer<typeof progettoPersonePartnerSetSchema>;

/* ── Fasi ─────────────────────────────────────────────────────────────────── */

export const faseCreateSchema = z.object({
  nome: z.string().trim().min(1).max(255),
  descrizione: z.string().trim().max(10_000).optional(),
  ordine: z.coerce.number().int().min(0).optional(),
  dataInizio: dataSchema.optional(),
  dataFine: dataSchema.optional(),
  stato: StatoFase.default('Aperta'),
  /** Cifra puntuale di fase; il totale di progetto è la loro somma. */
  valoreFase: importoSchema.optional(),
  numeroMarcheDaBollo: z.coerce.number().int().min(0).optional(),
  /** Fase riservata: valori e file visibili solo con ACCESS_RESERVED_PHASES. */
  riservata: z.boolean().default(false),
  assegnatarioIds: z.array(idSchema).default([]),
});
export type FaseCreate = z.infer<typeof faseCreateSchema>;

export const faseUpdateSchema = faseCreateSchema.partial().extend({
  // Idem `progettoUpdateSchema`: senza questa ridichiarazione, un PATCH che
  // omette uno di questi campi lo resetterebbe al default invece di lasciarlo
  // invariato (es. gli assegnatari gestiti a parte verrebbero svuotati).
  stato: StatoFase.optional(),
  riservata: z.boolean().optional(),
  assegnatarioIds: z.array(idSchema).optional(),
});
export type FaseUpdate = z.infer<typeof faseUpdateSchema>;

/**
 * Vista di una fase.
 *
 * Su una fase RISERVATA i due permessi viaggiano separati: `accessoValori`
 * copre il valore della fase, `accessoFile` la sua area documentale. Chi non ha
 * nessuno dei due vede solo nome e stato (`accessibile: false`).
 */
export const faseSchema = z.object({
  id: idSchema,
  progettoId: idSchema,
  nome: z.string(),
  descrizione: z.string().nullable(),
  ordine: z.number().int(),
  dataInizio: dataSchema.nullable(),
  dataFine: dataSchema.nullable(),
  stato: StatoFase,
  riservata: z.boolean(),
  /** `null` anche quando il valore c'è ma `accessoValori` è `false`. */
  valoreFase: importoSchema.nullable(),
  numeroMarcheDaBollo: z.number().int().nullable(),
  assegnatarioIds: z.array(idSchema),
  /**
   * `false` quando la fase è riservata e il richiedente non ha NESSUNO dei due
   * permessi: di lei restano solo nome e stato.
   */
  accessibile: z.boolean(),
  /** `false` quando gli importi della fase sono taciuti per mancanza di permesso. */
  accessoValori: z.boolean(),
  /** `false` quando l'area file della fase è negata (e con essa il verbale). */
  accessoFile: z.boolean(),
});
export type Fase = z.infer<typeof faseSchema>;

/* ── Note di fase (aggregate a livello progetto in visualizzazione) ───────── */

export const faseNotaCreateSchema = z.object({
  testo: z.string().trim().min(1).max(10_000),
});
export type FaseNotaCreate = z.infer<typeof faseNotaCreateSchema>;

export const faseNotaSchema = z.object({
  id: idSchema,
  faseProgettoId: idSchema,
  faseNome: z.string(),
  testo: z.string(),
  creatoDa: idSchema.nullable(),
  /** Nome dell'autore per il diario; `null` se l'utente è stato disattivato/rimosso. */
  autore: z.string().nullable(),
  creatoIl: dataOraSchema,
});
export type FaseNota = z.infer<typeof faseNotaSchema>;

/* ── Scadenze (indipendenti dalle fasi) ───────────────────────────────────── */

export const scadenzaCreateSchema = z.object({
  titolo: z.string().trim().min(1).max(255),
  descrizione: z.string().trim().max(10_000).optional(),
  data: dataSchema,
  stato: StatoScadenza.default('Da fare'),
  responsabileId: idSelezioneSchema('Seleziona un responsabile').optional(),
});
export type ScadenzaCreate = z.infer<typeof scadenzaCreateSchema>;

export const scadenzaUpdateSchema = scadenzaCreateSchema.partial().extend({
  // Idem `faseUpdateSchema`: senza questo, un PATCH che omette `stato` lo
  // resetterebbe a "Da fare" invece di lasciarlo invariato.
  stato: StatoScadenza.optional(),
});
export type ScadenzaUpdate = z.infer<typeof scadenzaUpdateSchema>;

export const scadenzaSchema = scadenzaCreateSchema.extend({
  id: idSchema,
  progettoId: idSchema,
  completataIl: dataOraSchema.nullable(),
  /** Derivato: giorni residui alla scadenza (negativo se già superata). */
  giorniResidui: z.number().int(),
});
export type Scadenza = z.infer<typeof scadenzaSchema>;

/** Scadenza arricchita col nome del responsabile, solo per la panoramica. */
export const scadenzaPanoramicaSchema = scadenzaSchema.extend({
  responsabileNome: z.string().nullable(),
});
export type ScadenzaPanoramica = z.infer<typeof scadenzaPanoramicaSchema>;

/* ── Panoramica (home) del progetto.9 ────────────────────────────────── */

export const progettoPanoramicaSchema = z.object({
  progetto: progettoSchema,
  /**
   * Somma calcolata dei valori fase. Le fasi riservate contribuiscono
   * solo per chi ha ACCESS_RESERVED_PHASES; agli altri il totale è al netto.
   */
  valorePraticaTotale: importoSchema,
  /** `true` se il totale esclude il contributo di fasi riservate non accessibili. */
  valorePraticaParziale: z.boolean(),
  scadenzeImminenti: z.array(scadenzaPanoramicaSchema),
  ultimeNote: z.array(faseNotaSchema),
  teamIds: z.array(idSchema),
});
export type ProgettoPanoramica = z.infer<typeof progettoPanoramicaSchema>;

/* ── Dashboard operatori.10 ──────────────────────────────────────────── */

export const dashboardOperatoreSchema = z.object({
  utenteId: idSchema,
  progettiAssegnati: z.number().int().min(0),
  /** Un progetto è "aperto" finché almeno una fase assegnata è Aperta. */
  progettiAperti: z.number().int().min(0),
  fasiAssegnate: z.number().int().min(0),
  fasiAperte: z.number().int().min(0),
});
export type DashboardOperatore = z.infer<typeof dashboardOperatoreSchema>;
